import { useContext, useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  DropdownMenu,
  DropdownToggle,
  FormGroup,
  UncontrolledDropdown,
  Modal,
  ModalBody,
  DropdownItem,
  Form,
  Badge,
  Alert,
} from "reactstrap";
import { isEmpty } from "lodash";
import { yupResolver } from "@hookform/resolvers/yup";
import { useSelector } from "react-redux";

import SiteTitle from "../../../helpers/SiteTitle/SiteTitle";
import Button from "./../../../components/Button/Button";
import Icon from "../../../components/Icon/Icon";
import ErrorMessage from "../../../helpers/ErrorMessage/ErrorMessage";
import PaginationComponent from "./../../../components/Pagination/Pagination";
import TableFilter from "../../../components/TableFilter/TableFilter";
import UserAvatar from "../../../components/UserAvatar/UserAvatar";
import PageHeader from "../../../components/PageHeader/PageHeader";
import SearchTable from "../../../components/SearchTable/SearchTable";
import AvatarCropper from "./../../../components/AvatarCropper/AvatarCropper";
import UserInfoItem from "../../../components/UserInfoItem/UserInfoItem";
import VerifiedBadge from "../../../components/VerifiedBadge/VerifiedBadge";
import DateTimePicker from "../../../components/DateTimePicker/DateTimePicker";
import { customerAgentContext } from "../../../context/AgentContext/CustomerAgentContext/CustomerAgentContext";
import {
  DataTable,
  DataTableBody,
  DataTableHead,
  DataTableItem,
  DataTableRow,
} from "./../../../components/DataTable/DataTable";
import { Block } from "./../../../components/Block/Block";
import { Col, Row } from "./../../../components/Grid/Grid";
import { convertDate, sumWallets } from "../../../utils";
import { areYouSureMessage } from "../../../utils/toastMessage/toastMessage";
import {
  changeUserPasswordValidation,
  createTransferTransactionForAgentValidation,
  createUserForAdminValidation,
  editUserForAdminValidation,
} from "../../../utils/validation/validation";
import Currency, {
  ConvertCurrency,
  ConvertCurrencyToText,
} from "../../../helpers/Currency/Currency";
import { convertCategoryToText, categoryItems } from "../../../utils/enum";

const CustomersAgent = () => {
  const loader = useSelector((state) => state.loader);

  const {
    customers,
    customer,
    setCustomer,
    filter,
    setFilter,
    sort,
    setSort,
    start,
    setStart,
    end,
    setEnd,
    pageCount,
    itemsCount,
    setItemsCount,
    pageNumber,
    setPageNumber,
    isShowModal,
    handleShowModal,
    handleHideModal,
    handleGetCustomers,
    handleCreateCustomer,
    handleEditCustomer,
    handleChangeCustomerActive,
    handleDeleteCustomer,
    handleUploadFile,
    handleAddMultipleUsers,
  } = useContext(customerAgentContext);

  useEffect(() => {
    handleGetCustomers();
  }, [start, end, pageNumber, itemsCount, sort]);

  // Validation
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    control,
    formState: { errors },
  } = useForm({
    defaultValues: {
      permissions: "111111",
    },
    resolver: yupResolver(
      isEmpty(customer)
        ? createUserForAdminValidation
        : editUserForAdminValidation
    ),
  });

  return (
    <>
      <SiteTitle title="مشتریان" />

      <PageHeader title="مشتریان" />

      <TableFilter
        setItemsCount={setItemsCount}
        itemsCount={itemsCount}
        setSort={setSort}
        sort={sort}
        setStart={setStart}
        start={start}
        setEnd={setEnd}
        end={end}
      />

      <Block>
        <DataTable className="card-stretch">
          <SearchTable
            firstItem={
              <Button
                color="primary"
                className="btn-icon"
                onClick={() => {
                  handleShowModal();
                  reset();
                }}
              >
                <Icon name="plus" />
              </Button>
            }
            endItem={
              <>
                <label
                  htmlFor="upload-xlsx"
                  className="btn-icon btn btn-success"
                >
                  <Icon name="file-xls" />
                  <input
                    onChange={(e) => {
                      handleAddMultipleUsers(e.target.files[0]);
                      e.target.value = "";
                    }}
                    id="upload-xlsx"
                    type="file"
                    className="d-none"
                    accept=".xlsx, .xls"
                  />
                </label>
              </>
            }
            filterPlaceholder="نام، نام خانوادگی، شماره موبایل، کد ملی و..."
            filter={filter}
            setFilter={setFilter}
            onSearch={handleGetCustomers}
          />
          <DataTableBody>
            <DataTableHead>
              <DataTableRow>
                <span className="sub-text">#</span>
              </DataTableRow>
              <DataTableRow>
                <span className="sub-text">مشتری</span>
              </DataTableRow>
              <DataTableRow size="mb">
                <span className="sub-text">شماره همراه</span>
              </DataTableRow>
              <DataTableRow size="md">
                <span className="sub-text">کد ملی</span>
              </DataTableRow>
              <DataTableRow size="lg">
                <span className="sub-text">کیف پول</span>
              </DataTableRow>
              <DataTableRow size="lg">
                <span className="sub-text">تاریخ ثبت نام</span>
              </DataTableRow>
              <DataTableRow size="md">
                <span className="sub-text">وضعیت</span>
              </DataTableRow>
              <DataTableRow />
            </DataTableHead>
            {/*Head*/}
            {customers.length > 0
              ? customers.map((item, index) => {
                  return (
                    <DataTableItem key={item._id}>
                      <DataTableRow>
                        <span>{index + 1}</span>
                      </DataTableRow>
                      <DataTableRow>
                        <div className="user-card">
                          <UserAvatar
                            {...(item?.avatar
                              ? { image: item?.avatar }
                              : {
                                  icon: "user-alt",
                                })}
                          />
                          <UserInfoItem
                            title={`${item.firstName} ${item.lastName}`}
                            subTitle={item.email}
                          />
                        </div>
                      </DataTableRow>
                      <DataTableRow size="mb">
                        <span>
                          {item.phoneNumber}{" "}
                          <VerifiedBadge
                            isVerified={item?.phoneNumberVerified}
                          />
                        </span>
                      </DataTableRow>
                      <DataTableRow size="md">
                        <span>{item.nationalNumber}</span>
                      </DataTableRow>
                      <DataTableRow size="lg">
                        <span>
                          <ConvertCurrency price={sumWallets(item.wallet)} />{" "}
                          <Currency />
                        </span>
                      </DataTableRow>
                      <DataTableRow size="lg">
                        <span>
                          {convertDate(item.createdAt, "YYYY/MM/DD - HH:mm")}
                        </span>
                      </DataTableRow>
                      <DataTableRow size="md">
                        <span
                          className={`badge badge-dot has-bg badge-${
                            item.isActive ? "success" : "danger"
                          }`}
                        >
                          {item.isActive ? "فعال" : "غیرفعال"}
                        </span>
                      </DataTableRow>
                      <DataTableRow className="nk-tb-col-tools">
                        <ul className="nk-tb-actions gx-1">
                          <li>
                            <UncontrolledDropdown>
                              <DropdownToggle
                                tag="a"
                                className="dropdown-toggle btn btn-icon btn-trigger"
                              >
                                <Icon name="more-h" />
                              </DropdownToggle>
                              <DropdownMenu
                                left
                                className="dropdown_max_height"
                              >
                                <ul className="link-list-opt no-bdr text-right">
                                  <li
                                    onClick={() => {
                                      reset();
                                      handleShowModal(item, setValue);
                                    }}
                                  >
                                    <DropdownItem tag="a" type="button">
                                      <Icon name="edit-alt-fill" />
                                      <span>ویرایش</span>
                                    </DropdownItem>
                                  </li>
                                  <li
                                    onClick={() =>
                                      setCustomer({
                                        ...item,
                                        isTransaction: true,
                                      })
                                    }
                                  >
                                    <DropdownItem tag="a" type="button">
                                      <Icon name="wallet-out" />
                                      <span>انتقال اعتبار</span>
                                    </DropdownItem>
                                  </li>
                                  <li
                                    onClick={() =>
                                      setCustomer({ ...item, isPassword: true })
                                    }
                                  >
                                    <DropdownItem tag="a" type="button">
                                      <Icon name="lock-alt-fill" />
                                      <span>تغییر کلمه عبور</span>
                                    </DropdownItem>
                                  </li>
                                  <li className="divider" />
                                  <li
                                    onClick={() =>
                                      areYouSureMessage(
                                        () =>
                                          handleChangeCustomerActive(item._id),
                                        `آیا از ${
                                          !item.isActive ? "فعال" : "غیرفعال"
                                        } کردن مشتری ${item.firstName} ${
                                          item.lastName
                                        } مطمئنید؟`
                                      )
                                    }
                                  >
                                    <DropdownItem
                                      tag="a"
                                      type="button"
                                      className={`text-${
                                        !item.isActive ? "success" : "danger"
                                      }`}
                                    >
                                      <Icon
                                        name={
                                          !item.isActive
                                            ? "user-check-fill"
                                            : "user-cross-fill"
                                        }
                                      />
                                      <span>
                                        {!item.isActive ? "فعال" : "غیرفعال"}
                                      </span>
                                    </DropdownItem>
                                  </li>
                                  <li
                                    onClick={() =>
                                      areYouSureMessage(
                                        () => handleDeleteCustomer(item._id),
                                        `آیا از حذف مشتری ${item.firstName} ${item.lastName} مطمئنید؟`
                                      )
                                    }
                                  >
                                    <DropdownItem
                                      tag="a"
                                      type="button"
                                      className="text-danger"
                                    >
                                      <Icon name="trash-fill" />
                                      <span>حذف</span>
                                    </DropdownItem>
                                  </li>
                                </ul>
                              </DropdownMenu>
                            </UncontrolledDropdown>
                          </li>
                        </ul>
                      </DataTableRow>
                    </DataTableItem>
                  );
                })
              : null}
          </DataTableBody>
          <div className="card-inner">
            {customers.length > 0 ? (
              <PaginationComponent
                pageCount={pageCount}
                currentPage={pageNumber}
                setPageNumber={setPageNumber}
              />
            ) : (
              <div className="text-center">
                <span className="text-silent">داده ای برای نمایش یافت نشد</span>
              </div>
            )}
          </div>
        </DataTable>
      </Block>

      <Modal
        isOpen={isShowModal}
        toggle={handleHideModal}
        className="modal-dialog-centered"
        size="lg"
      >
        <ModalBody>
          <span type="button" onClick={handleHideModal} className="close">
            <Icon name="cross-sm" />
          </span>
          <div className="p-2">
            <h5 className="title">
              {isEmpty(customer) ? `افزودن مشتری جدید` : `ویرایش مشتری`}
            </h5>
            <br />
            <Form
              className="is-alter"
              onSubmit={handleSubmit(
                isEmpty(customer) ? handleCreateCustomer : handleEditCustomer
              )}
            >
              <AvatarCropper
                onRemove={() => setValue("avatar", "")}
                value={watch("avatar")}
                onChange={(file) =>
                  handleUploadFile(file, (e) => setValue("avatar", e))
                }
              />

              <Row className="gy-4">
                <Col md="6">
                  <FormGroup>
                    <label className="form-label" htmlFor="firstName">
                      نام
                    </label>
                    <div className="form-control-wrap">
                      <input
                        type="text"
                        placeholder="نام را وارد نمایید"
                        className="form-control-lg form-control"
                        {...register("firstName")}
                      />
                      <ErrorMessage message={errors.firstName?.message} />
                    </div>
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <label className="form-label" htmlFor="lastName">
                      نام خانوادگی
                    </label>
                    <div className="form-control-wrap">
                      <input
                        type="text"
                        placeholder="نام خانوادگی را وارد نمایید"
                        className="form-control-lg form-control"
                        {...register("lastName")}
                      />
                      <ErrorMessage message={errors.lastName?.message} />
                    </div>
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <label className="form-label" htmlFor="phoneNumber">
                      شماره موبایل{" "}
                      {!isEmpty(customer) && (
                        <VerifiedBadge
                          isVerified={customer?.phoneNumberVerified}
                        />
                      )}
                    </label>
                    <div className="form-control-wrap">
                      <input
                        type="number"
                        placeholder="شماره موبایل را وارد نمایید"
                        className="form-control-lg form-control"
                        {...register("phoneNumber")}
                      />
                      <ErrorMessage message={errors.phoneNumber?.message} />
                    </div>
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <label className="form-label" htmlFor="nationalNumber">
                      کدملی
                    </label>
                    <div className="form-control-wrap">
                      <input
                        type="number"
                        placeholder="کدملی را وارد نمایید"
                        className="form-control-lg form-control"
                        {...register("nationalNumber")}
                      />
                      <ErrorMessage message={errors.nationalNumber?.message} />
                    </div>
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <label className="form-label" htmlFor="code">
                      کد
                    </label>
                    <div className="form-control-wrap">
                      <input
                        type="text"
                        placeholder="کد را وارد نمایید"
                        className="form-control-lg form-control"
                        {...register("code")}
                      />
                      <ErrorMessage message={errors.code?.message} />
                    </div>
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <label className="form-label" htmlFor="birthDate">
                      تاریخ تولد
                    </label>
                    <div className="form-control-wrap">
                      <Controller
                        control={control}
                        render={({ field: { onChange, value, onBlur } }) => (
                          <DateTimePicker
                            placeholder="تاریخ تولد را وارد نمایید"
                            initValue={value}
                            onChange={onChange}
                            onBlur={onBlur}
                            className="form-control-lg form-control"
                          />
                        )}
                        name="birthDate"
                      />
                      <ErrorMessage message={errors.birthDate?.message} />
                    </div>
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <label className="form-label" htmlFor="post">
                      سمت
                    </label>
                    <div className="form-control-wrap">
                      <input
                        type="text"
                        placeholder="سمت را وارد نمایید"
                        className="form-control-lg form-control"
                        {...register("post")}
                      />
                      <ErrorMessage message={errors.post?.message} />
                    </div>
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <label className="form-label" htmlFor="numberOfCovered">
                      تعداد افراد تحت پوشش
                    </label>
                    <div className="form-control-wrap">
                      <input
                        type="number"
                        placeholder="تعداد افراد تحت پوشش را وارد نمایید"
                        className="form-control-lg form-control"
                        {...register("numberOfCovered")}
                      />
                      <ErrorMessage message={errors.numberOfCovered?.message} />
                    </div>
                  </FormGroup>
                </Col>
                <Col md="12">
                  <FormGroup>
                    <label className="form-label" htmlFor="email">
                      پست الکترونیک{" "}
                      {!isEmpty(customer) && (
                        <VerifiedBadge isVerified={customer?.emailVerified} />
                      )}
                    </label>
                    <div className="form-control-wrap">
                      <input
                        type="email"
                        placeholder="پست الکترونیک را وارد نمایید"
                        className="form-control-lg form-control"
                        {...register("email")}
                      />
                      <ErrorMessage message={errors.email?.message} />
                    </div>
                  </FormGroup>
                </Col>
                {isEmpty(customer) && (
                  <>
                    <Col md="6">
                      <FormGroup>
                        <label className="form-label" htmlFor="password">
                          کلمه عبور
                        </label>
                        <div className="form-control-wrap">
                          <input
                            type="password"
                            placeholder="کلمه عبور را وارد نمایید"
                            className="form-control-lg form-control"
                            {...register("password")}
                          />
                          <ErrorMessage message={errors.password?.message} />
                        </div>
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <label className="form-label" htmlFor="confirmPassword">
                          تکرار کلمه عبور
                        </label>
                        <div className="form-control-wrap">
                          <input
                            type="password"
                            placeholder="تکرار کلمه عبور را وارد نمایید"
                            className="form-control-lg form-control"
                            {...register("confirmPassword")}
                          />
                          <ErrorMessage
                            message={errors.confirmPassword?.message}
                          />
                        </div>
                      </FormGroup>
                    </Col>
                  </>
                )}
                <Col size="12">
                  <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2">
                    <li>
                      <Button
                        color="primary"
                        size="lg"
                        type="submit"
                        loading={loader}
                      >
                        {isEmpty(customer) ? "افزودن" : "ویرایش"} کاربر
                      </Button>
                    </li>
                    <li>
                      <button
                        type="button"
                        onClick={handleHideModal}
                        className="link link-light"
                      >
                        انصراف
                      </button>
                    </li>
                  </ul>
                </Col>
              </Row>
            </Form>
          </div>
        </ModalBody>
      </Modal>

      <ChangePassword />
      <CreateTransferTransaction />
    </>
  );
};

export default CustomersAgent;

const ChangePassword = () => {
  const loader = useSelector((state) => state.loader);

  const { customer, setCustomer, handleChangeCustomerPassword } =
    useContext(customerAgentContext);

  // Validation
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(changeUserPasswordValidation),
  });

  useEffect(() => {
    if (customer.isPassword) reset();
  }, [customer]);

  return (
    <Modal
      isOpen={customer.isPassword}
      className="modal-dialog-centered"
      size="md"
      toggle={() => setCustomer({})}
    >
      <ModalBody>
        <span type="button" onClick={() => setCustomer({})} className="close">
          <Icon name="cross-sm" />
        </span>
        <div className="p-2">
          <h5 className="title">
            ویرایش کلمه عبور {customer?.firstName} {customer?.lastName}
          </h5>
          <br />
          <Form
            className="is-alter"
            onSubmit={handleSubmit(handleChangeCustomerPassword)}
          >
            <FormGroup>
              <label className="form-label" htmlFor="password">
                کلمه عبور <Badge color="primary">جدید</Badge>
              </label>
              <div className="form-control-wrap">
                <input
                  type="password"
                  placeholder="کلمه عبور را وارد نمایید"
                  className="form-control-lg form-control"
                  {...register("password")}
                />
                <ErrorMessage message={errors.password?.message} />
              </div>
            </FormGroup>
            <FormGroup>
              <label className="form-label" htmlFor="confirmPassword">
                تکرار کلمه عبور <Badge color="primary">جدید</Badge>
              </label>
              <div className="form-control-wrap">
                <input
                  type="password"
                  placeholder="تکرار کلمه عبور را وارد نمایید"
                  className="form-control-lg form-control"
                  {...register("confirmPassword")}
                />
                <ErrorMessage message={errors.confirmPassword?.message} />
              </div>
            </FormGroup>
            <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2">
              <li>
                <Button
                  color="primary"
                  size="lg"
                  type="submit"
                  loading={loader}
                >
                  ویرایش کلمه عبور
                </Button>
              </li>
              <li>
                <button
                  type="button"
                  onClick={() => setCustomer({})}
                  className="link link-light"
                >
                  انصراف
                </button>
              </li>
            </ul>
          </Form>
        </div>
      </ModalBody>
    </Modal>
  );
};

const CreateTransferTransaction = () => {
  const loader = useSelector((state) => state.loader);

  const { customer, setCustomer, handleCreateTransferTransaction } =
    useContext(customerAgentContext);

  // Validation
  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(createTransferTransactionForAgentValidation),
  });

  useEffect(() => {
    if (customer.isTransaction)
      reset({
        amount: {
          travel: 0,
          service: 0,
          household: 0,
          sustenance: 0,
          entertainment: 0,
          lifestyle: 0,
          education: 0,
          gift: 0,
        },
      });
  }, [customer]);

  return (
    <Modal
      isOpen={customer.isTransaction}
      className="modal-dialog-centered"
      size="md"
      toggle={() => setCustomer({})}
    >
      <ModalBody>
        <span type="button" onClick={() => setCustomer({})} className="close">
          <Icon name="cross-sm" />
        </span>
        <div className="p-2">
          <h5 className="title">
            انتقال اعتبار به {customer?.firstName} {customer?.lastName}
          </h5>
          <br />
          <Alert color="warning" className="text-center">
            با انتقال اعتبار امکان بازگشت اعتبار وجود ندارد، در وارد کردن مبلغ
            دقت نمایید{" "}
          </Alert>
          <Form
            className="is-alter"
            onSubmit={handleSubmit(handleCreateTransferTransaction)}
          >
            <Row className="gy-4">
              {categoryItems.map((item) => (
                <Col key={item} sm="6">
                  <FormGroup>
                    <label className="form-label" htmlFor={`amount.${item}`}>
                      مبلغ {convertCategoryToText(item)} <Currency />
                    </label>
                    <div className="form-control-wrap">
                      <input
                        type="number"
                        min={0}
                        placeholder={`مبلغ ${convertCategoryToText(
                          item
                        )} را وارد نمایید`}
                        className="form-control-lg form-control"
                        {...register(`amount.${item}`)}
                      />
                      {errors.amount && (
                        <ErrorMessage message={errors.amount[item]?.message} />
                      )}
                      {watch(`amount.${item}`) > 0 && (
                        <p className="mt-1">
                          <ConvertCurrencyToText
                            price={watch(`amount.${item}`)}
                            isCurrency
                          />
                        </p>
                      )}
                    </div>
                  </FormGroup>
                </Col>
              ))}
              <Col size="12">
                <FormGroup>
                  <label className="form-label" htmlFor="description">
                    توضیحات
                  </label>
                  <div className="form-control-wrap">
                    <textarea
                      placeholder="توضیحات را وارد نمایید"
                      className="form-control-lg form-control"
                      {...register("description")}
                    />
                    <ErrorMessage message={errors.description?.message} />
                  </div>
                </FormGroup>
              </Col>
              <Col size="12">
                <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2">
                  <li>
                    <Button
                      color="primary"
                      size="lg"
                      type="submit"
                      loading={loader}
                    >
                      انتقال اعتبار
                    </Button>
                  </li>
                  <li>
                    <button
                      type="button"
                      onClick={() => setCustomer({})}
                      className="link link-light"
                    >
                      انصراف
                    </button>
                  </li>
                </ul>
              </Col>
            </Row>
          </Form>
        </div>
      </ModalBody>
    </Modal>
  );
};
